import {
  Box,
  Typography,
  Tabs,
  Tab,
  Button,
  IconButton,
  CircularProgress,
  Stack,
  Pagination,
  useTheme,
} from "@mui/material";
import { useEffect, useState } from "react";
import {
  showErrorToast,
  showSuccessToast,
} from "../../../../../components/common/ShowToast";
import { DataGrid, GridColDef, GridRowModel } from "@mui/x-data-grid";
import apiService from "../../../../../service/Api";

import CustomTextField from "../../../../../components/ui/Textfield";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import type { RootState } from "../../../../..//store/Index";
import { useSelector } from "react-redux";
import PropTypes from "prop-types";

import { tokens } from "../../../../../theme";
import { ErrorContact, FileContact } from "../utils/processContacts";

interface VerifyContactsDataProps {
  parsedContacts: FileContact[];
  setParsedContacts: React.Dispatch<React.SetStateAction<FileContact[]>>;
  fetchContacts: () => void; // was: () => Promise<void>
  contactsFileRef: React.RefObject<HTMLInputElement | null>;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  errorContacts: ErrorContact[];
  removeErrorRecord: (id: number) => void;
  changed: (e: React.ChangeEvent<HTMLInputElement>) => Promise<void>;
  streamId: number | null;
}
const FLAGGED_PAGE_SIZE = 5;

// Strip everything but digits so "263 772 635 575", "263-772-635-575" and
// "+263772635575" all compare equal.
const normalizePhone = (value?: string | null): string =>
  (value ?? "").replace(/\D/g, "");

export const VerifyContactsData: React.FC<VerifyContactsDataProps> = ({
  parsedContacts,
  setParsedContacts,
  fetchContacts,
  contactsFileRef,
  setOpen,
  errorContacts,
  removeErrorRecord,
  changed,
  streamId,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [currentTab, setCurrentTab] = useState<string>("correct");
  const [flaggedPage, setFlaggedPage] = useState<number>(1);

  useEffect(() => {
    setFlaggedPage(1);
  }, [errorContacts.length]);

  const paginatedErrorContacts = errorContacts.slice(
    (flaggedPage - 1) * FLAGGED_PAGE_SIZE,
    flaggedPage * FLAGGED_PAGE_SIZE,
  );

  const totalFlaggedPages = Math.ceil(errorContacts.length / FLAGGED_PAGE_SIZE);

  const uploadContacts = async () => {
    if (!streamId) {
      showErrorToast(
        "Could not determine which memorial stream to attach contacts to.",
      );
      return;
    }

    setIsUploading(true);

    try {
      // Pull the current saved contacts for this stream right before
      // uploading, so re-uploading the same CSV (or a CSV with overlapping
      // rows) doesn't create duplicates. Matching on email OR phone covers
      // rows that only have a phone number (no email at all).
      const existingRes = await apiService.get<{
        results: Array<{ email: string | null; phone: string | null }>;
      }>(`/api/contacts/contact/?from_stream=${streamId}&page_size=1000`);

      const existingEmails = new Set(
        existingRes.data.results
          .map((c) => c.email?.toLowerCase())
          .filter((e): e is string => !!e),
      );
      const existingPhones = new Set(
        existingRes.data.results
          .map((c) => normalizePhone(c.phone))
          .filter((p) => p.length > 0),
      );

      const isDuplicate = (contact: FileContact) => {
        const email = contact.email?.toLowerCase();
        const phone = normalizePhone(contact.phone_number);
        return (
          (!!email && existingEmails.has(email)) ||
          (!!phone && existingPhones.has(phone))
        );
      };

      const duplicateCount = parsedContacts.filter(isDuplicate).length;
      const rowsToUpload = parsedContacts.filter((c) => !isDuplicate(c));

      if (rowsToUpload.length === 0) {
        showErrorToast(
          "All of these contacts already exist. Nothing new to upload.",
        );
        setIsUploading(false);
        return;
      }

      const newContacts = rowsToUpload.map((contact) => ({
        first_name: contact.first_name?.trim() || "",
        last_name: contact.last_name?.trim() || "",
        email: contact.email || null,
        phone: contact.phone_number || null,
      }));

      const response = await apiService.post<{
        created: number;
        skipped: number;
        errors: string[];
      }>("/api/contacts/contact/bulk-create-from-comments/", {
        stream_id: streamId,
        comments: newContacts,
      });

      const skippedTotal = response.data.skipped + duplicateCount;
      showSuccessToast(
        duplicateCount > 0
          ? `${response.data.created} contact(s) created, ${skippedTotal} skipped as duplicates.`
          : "Contacts uploaded",
      );

      setParsedContacts([]);
      fetchContacts();
      setOpen(false); // close the modal on a successful upload
    } catch (error: unknown) {
      console.error("Failed to upload contacts:", error);
      showErrorToast("Failed to upload contacts");
    } finally {
      setIsUploading(false);
    }
  };

  const openFileBrowseWindow = () => {
    contactsFileRef.current?.click();
  };

  const columns: GridColDef[] = [
    { field: "id", headerName: "Item", width: 90 },
    {
      field: "first_name",
      headerName: "First Name",
      width: 150,
      editable: true,
    },
    {
      field: "last_name",
      headerName: "Last Name",
      width: 150,
      editable: true,
    },
    {
      field: "email",
      headerName: "Email",
      width: 200,
      editable: true,
    },
    {
      field: "phone_number",
      headerName: "Phone Number",
      flex: 1,
      editable: true,
    },
  ];

  const handleProcessRowUpdate = (newRow: GridRowModel) => {
    setParsedContacts((prevRows) =>
      prevRows.map((row) =>
        row.id === newRow.id ? { ...row, ...(newRow as FileContact) } : row,
      ),
    );
    return newRow;
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
    setCurrentTab(newValue);
  };

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        height: "100%",
        bgcolor: "background.paper",
      }}
    >
      <Box
        sx={{
          width: "100%",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <input
          ref={contactsFileRef}
          style={{ width: "100%", height: "100%", display: "none" }}
          type="file"
          accept=".csv"
          onChange={(e) => changed(e)}
        />

        <Tabs
          value={currentTab}
          onChange={handleTabChange}
          indicatorColor="primary"
          aria-label="verify contacts tabs"
        >
          <Tab
            value={"correct"}
            label={
              <Box display="flex" alignItems="center">
                <span>Contacts</span>
                <Typography
                  variant="body2"
                  sx={{
                    borderRadius: "50%",
                    width: "25px",
                    height: "25px",
                    color: "#fff",
                    display: "grid",
                    placeContent: "center",
                    ml: 1,
                    backgroundColor: "#048b3dff",
                  }}
                >
                  {parsedContacts.length}
                </Typography>
              </Box>
            }
          />
          <Tab
            value={"error"}
            label={
              <Box display="flex" alignItems="center">
                <span>Flagged Contacts</span>
                <Typography
                  variant="body2"
                  sx={{
                    borderRadius: "50%",
                    width: "25px",
                    height: "25px",
                    color: "#fff",
                    display: "grid",
                    placeContent: "center",
                    ml: 1,
                    backgroundColor: "#b31212ff",
                  }}
                >
                  {errorContacts.length}
                </Typography>
              </Box>
            }
          />
        </Tabs>

        <Box>
          <Button
            variant="outlined"
            sx={{ mr: 2 }}
            onClick={openFileBrowseWindow}
            disabled={isUploading}
          >
            Choose File
          </Button>

          <Button
            variant="contained"
            sx={{ mr: 2 }}
            onClick={uploadContacts}
            disabled={isUploading}
          >
            {isUploading ? <CircularProgress /> : "Upload"}
          </Button>
        </Box>
      </Box>

      {currentTab === "correct" && (
        <Box sx={{ height: 500 }}>
          <DataGrid
            rows={parsedContacts}
            columns={columns}
            pageSizeOptions={[10, 25, 50, 100]}
            initialState={{
              pagination: { paginationModel: { pageSize: 25 } },
            }}
            disableRowSelectionOnClick
            processRowUpdate={handleProcessRowUpdate}
            sx={{
              height: "100%",
              zIndex: 1,
              "& .MuiDataGrid-row:hover": {
                backgroundColor: "transparent",
              },
              "& .MuiDataGrid-row": {
                backgroundColor: colors.primary[900],
              },
              cursor: "pointer",
            }}
          />
        </Box>
      )}

      {currentTab === "error" && (
        <Box sx={{ padding: 1 }}>
          {errorContacts.length === 0 && (
            <Typography variant="body2" sx={{ textAlign: "center" }}>
              No contacts to display at the moment
            </Typography>
          )}

          {errorContacts.length > 0 && (
            <>
              {paginatedErrorContacts.map((contact) => (
                <ErrorContactBox
                  key={contact.id}
                  contact={contact}
                  parsedContacts={parsedContacts}
                  removeErrorRecord={removeErrorRecord}
                  setParsedContacts={setParsedContacts}
                />
              ))}

              <Box
                sx={{ display: "flex", justifyContent: "center", mt: 2, mb: 1 }}
              >
                <Pagination
                  count={totalFlaggedPages}
                  page={flaggedPage}
                  onChange={(_, value) => setFlaggedPage(value)}
                  color="primary"
                  shape="rounded"
                />
              </Box>
            </>
          )}
        </Box>
      )}
    </Box>
  );
};

interface ErrorContactBoxProp {
  contact: ErrorContact;
  removeErrorRecord: (id: number) => void;
  parsedContacts: FileContact[];
  setParsedContacts: React.Dispatch<React.SetStateAction<FileContact[]>>;
}

const ErrorContactBox: React.FC<ErrorContactBoxProp> = ({
  contact,
  removeErrorRecord,
  parsedContacts,
  setParsedContacts,
}) => {
  const [correctedContact, setCorrectedContact] = useState<ErrorContact | null>(
    null,
  );
  const [isExpanded, setExpanded] = useState<boolean>(false);
  const theme = useTheme();
  const reduxTheme = useSelector((state: RootState) => state.theme);
  const colors = tokens(theme.palette.mode, reduxTheme);

  useEffect(() => {
    setCorrectedContact(contact);
  }, [contact]);

  const handleContactChange = (field: keyof ErrorContact, value: string) => {
    setCorrectedContact((prev) => {
      if (prev === null) {
        return null;
      }
      return { ...prev, [field]: value };
    });
  };

  const fixRecord = () => {
    if (!correctedContact?.first_name || !correctedContact?.last_name) {
      showErrorToast(
        "Cannot fix this record. Please include a first_name and last_name",
      );
      return;
    }

    if (!correctedContact.email && !correctedContact.phone_number) {
      showErrorToast(
        "Cannot fix this record. Please include an email or phone number",
      );
      return;
    }

    const fixedRecord: FileContact = {
      id: parsedContacts.length + 1,
      first_name: correctedContact.first_name,
      last_name: correctedContact.last_name,
      email: correctedContact.email,
      phone_number: correctedContact.phone_number,
    };

    setParsedContacts([...parsedContacts, fixedRecord]);
    removeErrorRecord(contact.id);
    showSuccessToast("Record Corrected");
  };

  return (
    <Stack
      sx={{
        backgroundColor: colors.primary[800],
        borderRadius: ".5rem",
        width: "95%",
        marginInline: "auto",
        padding: "1.2rem",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderBottom: isExpanded ? "1px solid white" : undefined,
          mb: isExpanded ? 3 : undefined,
        }}
      >
        {!contact?.unknownFieldsArray ? (
          <Typography
            variant="body2"
            sx={{
              padding: "0.5rem",
              backgroundColor: "#b31212ff",
              color: "#fff",
              borderRadius: ".5rem",
              fontFamily: '"Mulish", sans-serif',
              fontWeight: "bold",
            }}
          >
            Missing fields
          </Typography>
        ) : (
          <Typography
            variant="body2"
            sx={{
              padding: "0.5rem",
              backgroundColor: "#b31212ff",
              color: "#fff",
              borderRadius: ".5rem",
              fontFamily: '"Mulish", sans-serif',
              fontWeight: "bold",
            }}
          >
            Unknown fields
          </Typography>
        )}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
          }}
        >
          <Button variant="text" sx={{ fontWeight: 600 }} onClick={fixRecord}>
            Mark as Fixed
          </Button>
          <IconButton
            sx={{
              mr: 1,
              display: "block",
              "&:hover": { color: "#a60b0bff" },
            }}
            onClick={() => removeErrorRecord(contact.id)}
          >
            <DeleteOutlineOutlinedIcon />
          </IconButton>

          <IconButton
            sx={{
              "&:hover": { color: colors.primary[400] },
            }}
            onClick={() => setExpanded(!isExpanded)}
          >
            {!isExpanded ? <ExpandMoreIcon /> : <ExpandLessIcon />}
          </IconButton>
        </Box>
      </Box>

      {isExpanded && (
        <>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "start",
              gap: 5,
              marginBottom: "1.2rem",
            }}
          >
            <Typography
              variant="body2"
              sx={{
                padding: "1rem",
                backgroundColor: colors.primary[900],
                borderRadius: "0.5rem",
                fontSize: ".9rem",
                width: "8rem",
              }}
            >
              first_name
            </Typography>

            <CustomTextField
              value={correctedContact?.first_name}
              onChange={(e) =>
                handleContactChange("first_name", e.target.value)
              }
              fullWidth={true}
              variant="outlined"
              required
              size="medium"
            />
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "start",
              gap: 5,
              marginBottom: "1.2rem",
            }}
          >
            <Typography
              variant="body2"
              sx={{
                padding: "1rem",
                backgroundColor: colors.primary[900],
                borderRadius: "0.5rem",
                fontSize: ".9rem",
                width: "8rem",
              }}
            >
              last_name
            </Typography>

            <CustomTextField
              value={correctedContact?.last_name}
              onChange={(e) => handleContactChange("last_name", e.target.value)}
              fullWidth={true}
              label=""
              variant="outlined"
              required
              size="medium"
            />
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "start",
              gap: 5,
              marginBottom: "1.2rem",
            }}
          >
            <Typography
              variant="body2"
              sx={{
                padding: "1rem",
                backgroundColor: colors.primary[900],
                borderRadius: "0.5rem",
                fontSize: ".9rem",
                width: "8rem",
              }}
            >
              email
            </Typography>

            <CustomTextField
              value={correctedContact?.email}
              onChange={(e) => handleContactChange("email", e.target.value)}
              fullWidth={true}
              label=""
              variant="outlined"
              size="medium"
            />
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "start",
              gap: 5,
              marginBottom: "1.2rem",
            }}
          >
            <Typography
              variant="body2"
              sx={{
                padding: "1rem",
                backgroundColor: colors.primary[900],
                borderRadius: "0.5rem",
                fontSize: ".9rem",
                width: "8rem",
              }}
            >
              phone_number
            </Typography>

            <CustomTextField
              value={correctedContact?.phone_number}
              onChange={(e) =>
                handleContactChange("phone_number", e.target.value)
              }
              fullWidth={true}
              label=""
              variant="outlined"
              size="medium"
            />
          </Box>

          {contact.unknownFieldsArray !== undefined &&
            contact.unknownFieldsArray.map((field, index) => {
              return (
                <Box
                  key={`${field}-${index}`}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "start",
                    gap: 5,
                    marginBottom: "1.2rem",
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      padding: "1rem",
                      backgroundColor: colors.primary[900],
                      borderRadius: "0.5rem",
                      fontSize: ".9rem",
                      width: "8rem",
                    }}
                  >
                    Unknown
                  </Typography>

                  <CustomTextField
                    value={field}
                    fullWidth={true}
                    label=""
                    variant="outlined"
                    size="medium"
                  />
                </Box>
              );
            })}
        </>
      )}
    </Stack>
  );
};

const fileContactShape = PropTypes.shape({
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  first_name: PropTypes.string,
  last_name: PropTypes.string,
  email: PropTypes.string,
  phone_number: PropTypes.string,
});

const errorContactShape = PropTypes.shape({
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  first_name: PropTypes.string,
  last_name: PropTypes.string,
  email: PropTypes.string,
  phone_number: PropTypes.string,
  unknownFieldsArray: PropTypes.arrayOf(PropTypes.string),
});

VerifyContactsData.propTypes = {
  parsedContacts: PropTypes.arrayOf(fileContactShape).isRequired,
  setParsedContacts: PropTypes.func.isRequired,
  fetchContacts: PropTypes.func.isRequired,
  contactsFileRef: PropTypes.shape({ current: PropTypes.any }).isRequired,
  setOpen: PropTypes.func.isRequired,
  errorContacts: PropTypes.arrayOf(errorContactShape).isRequired,
  removeErrorRecord: PropTypes.func.isRequired,
  changed: PropTypes.func.isRequired,
};

ErrorContactBox.propTypes = {
  contact: errorContactShape.isRequired,
  removeErrorRecord: PropTypes.func.isRequired,
  parsedContacts: PropTypes.arrayOf(fileContactShape).isRequired,
  setParsedContacts: PropTypes.func.isRequired,
};